/**
 * HTML-based Icon component stubs for web platform
 * Uses Font Awesome, Material Design Icons, and Ionicons via real HTML/CSS
 * Supports Ionicons, MaterialIcons, MaterialCommunityIcons, and FontAwesome5 APIs
 */
import React from 'react';
import { View } from 'react-native';

interface IconProps {
  name: string;
  size?: number;
  color?: string;
  style?: any;
  testID?: string;
  [key: string]: any;
}

/**
 * Mapping from Ionicons names to Font Awesome 6 icons
 */
const IonIconsToFontAwesome: {[key: string]: string} = {
  'arrow-back': 'fa-arrow-left',
  'arrow-back-outline': 'fa-arrow-left',
  'arrow-forward': 'fa-arrow-right',
  'arrow-forward-outline': 'fa-arrow-right',
  'arrow-up': 'fa-arrow-up',
  'arrow-down': 'fa-arrow-down',
  'chevron-back': 'fa-chevron-left',
  'chevron-back-outline': 'fa-chevron-left',
  'chevron-forward': 'fa-chevron-right',
  'chevron-forward-outline': 'fa-chevron-right',
  'chevron-down': 'fa-chevron-down',
  'chevron-down-outline': 'fa-chevron-down',
  'chevron-up': 'fa-chevron-up',
  'chevron-up-outline': 'fa-chevron-up',
  'close': 'fa-xmark',
  'close-outline': 'fa-xmark',
  'menu': 'fa-bars',
  'menu-outline': 'fa-bars',
  'home': 'fa-house',
  'home-outline': 'fa-house',
  'search': 'fa-magnifying-glass',
  'search-outline': 'fa-magnifying-glass',
  'settings': 'fa-gear',
  'settings-outline': 'fa-gear',
  'person': 'fa-user',
  'person-outline': 'fa-user',
  'people': 'fa-users',
  'people-outline': 'fa-users',
  'share-outline': 'fa-share-nodes',
  'share': 'fa-share-nodes',
  'heart': 'fa-heart',
  'heart-outline': 'fa-heart-open',
  'star': 'fa-star',
  'star-outline': 'fa-star-open',
  'thumbs-up': 'fa-thumbs-up',
  'thumbs-up-outline': 'fa-thumbs-up',
  'thumbs-down': 'fa-thumbs-down',
  'thumbs-down-outline': 'fa-thumbs-down',
  'checkmark': 'fa-check',
  'checkmark-circle': 'fa-circle-check',
  'checkmark-done': 'fa-check-double',
  'close-circle': 'fa-circle-xmark',
  'help-circle': 'fa-circle-question',
  'help-circle-outline': 'fa-circle-question',
  'information-circle': 'fa-circle-info',
  'information-circle-outline': 'fa-circle-info',
  'warning': 'fa-triangle-exclamation',
  'warning-outline': 'fa-triangle-exclamation',
  'alert': 'fa-bell',
  'alert-circle': 'fa-circle-exclamation',
  'alert-circle-outline': 'fa-circle-exclamation',
  'notifications': 'fa-bell',
  'notifications-outline': 'fa-bell',
  'document': 'fa-file',
  'document-outline': 'fa-file',
  'download': 'fa-download',
  'download-outline': 'fa-download',
  'upload': 'fa-upload',
  'upload-outline': 'fa-upload',
  'trash': 'fa-trash-can',
  'trash-outline': 'fa-trash-can',
  'create': 'fa-pen',
  'create-outline': 'fa-pen',
  'pencil': 'fa-pencil',
  'copy': 'fa-copy',
  'copy-outline': 'fa-copy',
  'clipboard': 'fa-clipboard',
  'clipboard-outline': 'fa-clipboard',
  'call': 'fa-phone',
  'call-outline': 'fa-phone',
  'mail': 'fa-envelope',
  'mail-outline': 'fa-envelope',
  'mail-open': 'fa-envelope-open',
  'mail-open-outline': 'fa-envelope-open',
  'chatbubble': 'fa-message',
  'chatbubble-outline': 'fa-message',
  'chatbubbles': 'fa-messages',
  'chatbubbles-outline': 'fa-messages',
  'logo-facebook': 'fa-facebook',
  'logo-twitter': 'fa-twitter',
  'logo-instagram': 'fa-instagram',
  'logo-linkedin': 'fa-linkedin',
  'logo-google': 'fa-google',
  'logo-whatsapp': 'fa-whatsapp',
  'logo-github': 'fa-github',
};

/**
 * Mapping from MaterialIcons names to Font Awesome 6 icons
 */
const MaterialIconsToFontAwesome: {[key: string]: string} = {
  'check': 'fa-check',
  'close': 'fa-xmark',
  'clear': 'fa-xmark',
  'delete': 'fa-trash-can',
  'remove': 'fa-xmark',
  'edit': 'fa-pen',
  'pencil': 'fa-pencil',
  'add': 'fa-plus',
  'plus': 'fa-plus',
  'minus': 'fa-minus',
  'search': 'fa-magnifying-glass',
  'settings': 'fa-gear',
  'info': 'fa-info',
  'help': 'fa-circle-question',
  'warning': 'fa-triangle-exclamation',
  'error': 'fa-circle-exclamation',
  'favorite': 'fa-heart',
  'favorite-border': 'fa-heart-open',
  'star': 'fa-star',
  'star-border': 'fa-star-open',
  'star-half': 'fa-star-half-stroke',
  'thumb-up': 'fa-thumbs-up',
  'thumb-down': 'fa-thumbs-down',
  'thumbs-up': 'fa-thumbs-up',
  'thumbs-down': 'fa-thumbs-down',
  'arrow-back': 'fa-arrow-left',
  'arrow-forward': 'fa-arrow-right',
  'arrow-upward': 'fa-arrow-up',
  'arrow-downward': 'fa-arrow-down',
  'chevron-left': 'fa-chevron-left',
  'chevron-right': 'fa-chevron-right',
  'chevron-up': 'fa-chevron-up',
  'chevron-down': 'fa-chevron-down',
  'menu': 'fa-bars',
  'hamburger': 'fa-bars',
  'more-vert': 'fa-ellipsis-vertical',
  'more-horiz': 'fa-ellipsis',
  'home': 'fa-house',
  'person': 'fa-user',
  'people': 'fa-users',
  'group': 'fa-users',
  'call': 'fa-phone',
  'phone': 'fa-phone',
  'mail': 'fa-envelope',
  'email': 'fa-envelope',
  'message': 'fa-envelope',
  'chat': 'fa-message',
  'chat-bubble': 'fa-message',
  'comment': 'fa-comment',
  'forum': 'fa-comments',
  'share': 'fa-share-nodes',
  'share-alt': 'fa-share-nodes',
  'folder': 'fa-folder',
  'folder-open': 'fa-folder-open',
  'file': 'fa-file',
  'document': 'fa-file',
  'image': 'fa-image',
  'picture': 'fa-image',
  'photo': 'fa-photo',
  'video': 'fa-video',
  'videocam': 'fa-video',
  'music-note': 'fa-music',
  'attachment': 'fa-paperclip',
  'download': 'fa-download',
  'upload': 'fa-upload',
  'done': 'fa-check',
  'done-all': 'fa-check-double',
  'check-circle': 'fa-circle-check',
  'visibility': 'fa-eye',
  'visibility-off': 'fa-eye-slash',
  'eye': 'fa-eye',
  'lock': 'fa-lock',
  'unlock': 'fa-unlock',
  'vpn-key': 'fa-key',
  'fingerprint': 'fa-fingerprint',
  'verified': 'fa-circle-check',
  'verified-user': 'fa-circle-check',
  'location-on': 'fa-location-dot',
  'location-off': 'fa-location-crosshairs-slash',
  'map': 'fa-map',
  'place': 'fa-location-dot',
  'directions': 'fa-directions',
  'navigation': 'fa-compass',
  'compass': 'fa-compass',
  'store': 'fa-shop',
  'hotel': 'fa-bed',
  'restaurant': 'fa-utensils',
  'cafe': 'fa-mug-hot',
  'parking': 'fa-p',
  'sentiment-very-satisfied': 'fa-face-grin-stars',
  'sentiment-satisfied': 'fa-face-smile',
  'sentiment-neutral': 'fa-face-meh',
  'sentiment-dissatisfied': 'fa-face-frown',
  'sentiment-very-dissatisfied': 'fa-face-angry',
  'business': 'fa-briefcase',
  'card-giftcard': 'fa-gift',
  'card-membership': 'fa-credit-card',
  'card-travel': 'fa-credit-card',
  'confirmation-number': 'fa-ticket',
  'event': 'fa-calendar',
  'today': 'fa-calendar-day',
  'access-time': 'fa-clock',
  'schedule': 'fa-calendar',
  'date-range': 'fa-calendar',
  'event-note': 'fa-calendar-check',
  'repeat': 'fa-repeat',
  'trending-up': 'fa-arrow-up',
  'trending-down': 'fa-arrow-down',
  'bar-chart': 'fa-bar-chart',
  'pie-chart': 'fa-pie-chart',
  'assessment': 'fa-bar-chart',
  'monetization-on': 'fa-money-bill',
  'attach-money': 'fa-money-bill',
  'currency': 'fa-money-bill',
  'shopping-cart': 'fa-shopping-cart',
  'shopping-bag': 'fa-bag-shopping',
  'local-offer': 'fa-tag',
  'local-activity': 'fa-ticket',
  'local-dining': 'fa-utensils',
  'local-drink': 'fa-glass-water',
  'local-florist': 'fa-flower',
  'local-gas-station': 'fa-gas-pump',
  'local-grocery-store': 'fa-bag-shopping',
  'local-hospital': 'fa-hospital',
  'local-hotel': 'fa-bed',
  'local-library': 'fa-book',
  'local-mall': 'fa-bag-shopping',
  'local-movies': 'fa-film',
  'local-parking': 'fa-p',
  'local-pharmacy': 'fa-pills',
  'local-pizza': 'fa-pizza-slice',
  'local-play': 'fa-play',
  'local-post-office': 'fa-envelope',
  'local-printshop': 'fa-print',
  'local-see': 'fa-eye',
  'local-shipping': 'fa-truck',
  'local-taxi': 'fa-taxi',
  'loyalty': 'fa-heart',
  'redeem': 'fa-gift',
  'check-box': 'fa-square-check',
  'check-box-outline-blank': 'fa-square',
  'radio-button-checked': 'fa-circle-dot',
  'radio-button-unchecked': 'fa-circle',
  'toggle-off': 'fa-toggle-off',
  'toggle-on': 'fa-toggle-on',
  'unfold-more': 'fa-expand',
  'unfold-less': 'fa-compress',
  'fullscreen': 'fa-expand',
  'fullscreen-exit': 'fa-compress',
  'zoom-in': 'fa-magnifying-glass-plus',
  'zoom-out': 'fa-magnifying-glass-minus',
  'brightness-high': 'fa-sun',
  'brightness-low': 'fa-moon',
  'fiber-manual-record': 'fa-circle',
};

/**
 * Ionicons component - Renders using Font Awesome icons
 */
class Ionicons extends React.Component<IconProps> {
  render() {
    const { name, size = 24, color = 'black', style, testID, ...rest } = this.props;
    const fontAwesomeIcon = IonIconsToFontAwesome[name] || name;
    
    return (
      <View
        testID={testID}
        style={[
          {
            width: size,
            height: size,
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex' as any,
          },
          style,
        ]}
        {...rest}>
        <i
          className={`fa fa-fw ${fontAwesomeIcon}`}
          style={{
            fontSize: size,
            color: color,
            lineHeight: `${size}px`,
            textAlign: 'center' as any,
          }}
        />
      </View>
    );
  }
}

/**
 * MaterialIcons component - Renders using Font Awesome icons
 */
class MaterialIcons extends React.Component<IconProps> {
  render() {
    const { name, size = 24, color = 'black', style, testID, ...rest } = this.props;
    const fontAwesomeIcon = MaterialIconsToFontAwesome[name] || name;
    
    return (
      <View
        testID={testID}
        style={[
          {
            width: size,
            height: size,
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex' as any,
          },
          style,
        ]}
        {...rest}>
        <i
          className={`fa fa-fw ${fontAwesomeIcon}`}
          style={{
            fontSize: size,
            color: color,
            lineHeight: `${size}px`,
            textAlign: 'center' as any,
          }}
        />
      </View>
    );
  }
}

/**
 * MaterialCommunityIcons component - Renders using Font Awesome icons
 */
class MaterialCommunityIcons extends React.Component<IconProps> {
  render() {
    const { name, size = 24, color = 'black', style, testID, ...rest } = this.props;
    const fontAwesomeIcon = MaterialIconsToFontAwesome[name] || name;
    
    return (
      <View
        testID={testID}
        style={[
          {
            width: size,
            height: size,
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex' as any,
          },
          style,
        ]}
        {...rest}>
        <i
          className={`fa fa-fw ${fontAwesomeIcon}`}
          style={{
            fontSize: size,
            color: color,
            lineHeight: `${size}px`,
            textAlign: 'center' as any,
          }}
        />
      </View>
    );
  }
}

/**
 * FontAwesome5 component - Direct Font Awesome support
 */
class FontAwesome5 extends React.Component<IconProps> {
  render() {
    const { name, size = 24, color = 'black', style, testID, ...rest } = this.props;
    const faIcon = `fa-${name}`;
    
    return (
      <View
        testID={testID}
        style={[
          {
            width: size,
            height: size,
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex' as any,
          },
          style,
        ]}
        {...rest}>
        <i
          className={`fa fa-fw ${faIcon}`}
          style={{
            fontSize: size,
            color: color,
            lineHeight: `${size}px`,
            textAlign: 'center' as any,
          }}
        />
      </View>
    );
  }
}

// Default export
export default Ionicons;

// Also export named exports
export { Ionicons, MaterialIcons, MaterialCommunityIcons, FontAwesome5 };
